import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { submitGrievance } from '../services/api';
import { importantContacts, adminContact } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { Send, MessageSquare, Phone, MapPin, AlertCircle, CheckCircle, UserCheck, Shield } from 'lucide-react';

const GrievancePage = () => {
  const { t, getText, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    category: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const categories = [
    { value: 'road', label: { en: 'Road & Infrastructure', pa: 'ਸੜਕ ਅਤੇ ਢਾਂਚਾ' } },
    { value: 'water', label: { en: 'Water Supply', pa: 'ਪਾਣੀ ਦੀ ਸਪਲਾਈ' } },
    { value: 'electricity', label: { en: 'Electricity', pa: 'ਬਿਜਲੀ' } },
    { value: 'sanitation', label: { en: 'Sanitation & Cleanliness', pa: 'ਸਫ਼ਾਈ' } },
    { value: 'health', label: { en: 'Health Services', pa: 'ਸਿਹਤ ਸੇਵਾਵਾਂ' } },
    { value: 'education', label: { en: 'Education', pa: 'ਸਿੱਖਿਆ' } },
    { value: 'other', label: { en: 'Other', pa: 'ਹੋਰ' } }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.category || !formData.message) {
      toast.error(language === 'pa' ? 'ਕਿਰਪਾ ਸਾਰੇ ਲੋੜੀਂਦੇ ਖੇਤਰ ਭਰੋ' : 'Please fill all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await submitGrievance({
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        category: formData.category,
        subject: formData.subject || null,
        message: formData.message
      });
      
      setIsSubmitted(true);
      toast.success(language === 'pa' ? 'ਤੁਹਾਡੀ ਸ਼ਿਕਾਇਤ ਜਮ੍ਹਾਂ ਹੋ ਗਈ ਹੈ' : 'Your grievance has been submitted successfully');
      
      setTimeout(() => {
        setFormData({ name: '', phone: '', email: '', category: '', subject: '', message: '' });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting grievance:', error);
      toast.error(language === 'pa' ? 'ਕੁਝ ਗਲਤ ਹੋਇਆ, ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ' : 'Something went wrong, please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Hero */}
      <section className="bg-gradient-to-r from-amber-500 to-orange-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {language === 'pa' ? 'ਸ਼ਿਕਾਇਤ / ਸੁਝਾਅ' : 'Grievance / Suggestions'}
            </h1>
            <p className="text-amber-100">
              {language === 'pa' ? 'ਪਿੰਡ ਦੇ ਅਧਿਕਾਰੀਆਂ ਨਾਲ ਸੰਪਰਕ ਕਰੋ' : 'Contact Village Officials'}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-6">
            {/* Important Contacts */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-amber-500" />
                  {language === 'pa' ? 'ਮਹੱਤਵਪੂਰਨ ਸੰਪਰਕ' : 'Important Contacts'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {importantContacts.map((contact) => (
                  <div key={contact.id} className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                    <UserCheck className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-xs text-amber-600">{getText(contact.title)}</p>
                      <p className="font-medium text-gray-900">{contact.name}</p>
                      <a 
                        href={`tel:${contact.phone.replace(/\s/g, '')}`} 
                        className="text-sm text-gray-500 hover:text-amber-600 flex items-center gap-1"
                      >
                        <Phone className="h-3 w-3" />
                        {contact.phone}
                      </a>
                    </div>
                  </div>
                ))}
                
                {/* Village Location */}
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <MapPin className="h-5 w-5 text-gray-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">{language === 'pa' ? 'ਪਿੰਡ ਦਾ ਪਤਾ' : 'Village Address'}</p>
                    <p className="text-sm text-gray-500">
                      {language === 'pa' ? 'ਮਰਹਾਣਾ, ਤਰਨ ਤਾਰਨ, ਪੰਜਾਬ - 143410' : 'Marhana, Tarn Taran, Punjab - 143410'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Website Admin */}
            <Card className="border-0 shadow-lg bg-gradient-to-br from-gray-800 to-gray-900 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center">
                    <Shield className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">{getText(adminContact.role)}</p>
                    <p className="font-bold text-lg">{adminContact.name}</p>
                  </div>
                </div>
                <a 
                  href={`tel:${adminContact.phone.replace(/\s/g, '')}`}
                  className="flex items-center justify-center gap-2 w-full py-3 bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors font-medium"
                >
                  <Phone className="h-5 w-5" />
                  {adminContact.phone}
                </a>
                <p className="text-gray-400 text-xs mt-3 text-center">
                  {language === 'pa' 
                    ? 'ਵੈੱਬਸਾਈਟ ਵਿੱਚ ਆਪਣੀ ਜਾਣਕਾਰੀ ਜੋੜਨ ਲਈ ਸੰਪਰਕ ਕਰੋ'
                    : 'Contact to add your info on website'}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500 to-orange-500 text-white">
              <CardContent className="p-6">
                <AlertCircle className="h-8 w-8 mb-4" />
                <h3 className="font-bold text-lg mb-2">
                  {language === 'pa' ? 'ਮਹੱਤਵਪੂਰਨ ਨੋਟ' : 'Important Note'}
                </h3>
                <p className="text-sm text-amber-100">
                  {language === 'pa' 
                    ? 'ਤੁਹਾਡੀ ਸ਼ਿਕਾਇਤ ਸਰਪੰਚ ਅਤੇ ਪਿੰਡ ਦੇ ਅਧਿਕਾਰੀਆਂ ਨੂੰ ਭੇਜੀ ਜਾਵੇਗੀ'
                    : 'Your grievance will be forwarded to Sarpanch and village officials'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Grievance Form */}
          <Card className="lg:col-span-2 border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-amber-500" />
                {language === 'pa' ? 'ਸ਼ਿਕਾਇਤ ਫਾਰਮ' : 'Grievance Form'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                    <CheckCircle className="h-8 w-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {language === 'pa' ? 'ਸ਼ਿਕਾਇਤ ਜਮ੍ਹਾਂ ਹੋ ਗਈ!' : 'Grievance Submitted!'}
                  </h3>
                  <p className="text-gray-500">
                    {language === 'pa' 
                      ? 'ਅਸੀਂ ਜਲਦੀ ਹੀ ਤੁਹਾਡੇ ਨਾਲ ਸੰਪਰਕ ਕਰਾਂਗੇ'
                      : 'We will contact you soon regarding your grievance'}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('yourName')} <span className="text-red-500">*</span>
                      </label>
                      <Input 
                        placeholder={language === 'pa' ? 'ਤੁਹਾਡਾ ਨਾਮ ਲਿਖੋ' : 'Enter your name'}
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('yourPhone')} <span className="text-red-500">*</span>
                      </label>
                      <Input 
                        type="tel"
                        placeholder="+91 98765 43210"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'pa' ? 'ਈਮੇਲ' : 'Email'}
                      </label>
                      <Input 
                        type="email"
                        placeholder="email@example.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'pa' ? 'ਸ਼੍ਰੇਣੀ' : 'Category'} <span className="text-red-500">*</span>
                      </label>
                      <Select value={formData.category} onValueChange={(val) => handleChange('category', val)}>
                        <SelectTrigger>
                          <SelectValue placeholder={language === 'pa' ? 'ਸ਼੍ਰੇਣੀ ਚੁਣੋ' : 'Select category'} />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>
                              {cat.label[language]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('subject')}
                    </label>
                    <Input 
                      placeholder={language === 'pa' ? 'ਵਿਸ਼ਾ ਲਿਖੋ' : 'Brief subject'}
                      value={formData.subject}
                      onChange={(e) => handleChange('subject', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('message')} <span className="text-red-500">*</span>
                    </label>
                    <Textarea 
                      placeholder={language === 'pa' ? 'ਆਪਣੀ ਸ਼ਿਕਾਇਤ ਵਿਸਤਾਰ ਨਾਲ ਲਿਖੋ...' : 'Describe your grievance in detail...'}
                      rows={5}
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                        {language === 'pa' ? 'ਭੇਜਿਆ ਜਾ ਰਿਹਾ ਹੈ...' : 'Submitting...'}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="h-4 w-4" />
                        {t('submit')}
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GrievancePage;
